import { reportarErro } from '@/api/reportar-erro';

/**
 * Cliente HTTP único da aplicação (F0-5).
 *
 * Regra 2 do ESTRUTURA-PASTAS §2: nenhum `fetch` fora de `api/`. Tudo passa por
 * aqui — é o único lugar que sabe de sessão, de erro e do que fazer no 401.
 *
 * A assinatura `(url, init)` é a que o orval v8 gera para um mutator: ele chama
 * `httpClient<T>(getUrl(), { ...options, method })`.
 */

/** Vazio em dev: o proxy do Vite encaminha /v1 e mantém tudo same-origin. */
const BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * URL absoluta de um endpoint que devolve ARQUIVO, para o browser buscar sozinho.
 *
 * ⚠️ Rota de arquivo (PDF) não pode passar pelo `httpClient`: ele faz
 * `resposta.text()` e `JSON.parse` incondicionais, e o binário quebraria no
 * parse. O cliente gerado pelo orval tipa `/pdf` como `Promise<void>` e cairia
 * nessa armadilha — por isso o download é feito abrindo esta URL, e não pelo
 * hook.
 *
 * A sessão viaja igual: é cookie httpOnly e a requisição é same-origin (proxy
 * do Vite em dev, mesmo domínio em produção), então o browser o envia sem que
 * ninguém precise montar header.
 */
export function urlDeArquivo(caminho: string): string {
  return `${BASE}${caminho}`;
}

/**
 * Baixa um arquivo como `Blob`, para quando a tela precisa do CONTEÚDO e não
 * de um endereço para abrir.
 *
 * Existe por causa do compartilhamento nativo (`navigator.share` com arquivos):
 * ali o WhatsApp recebe o PDF de verdade, e para isso é preciso ter os bytes em
 * mãos. Fora esse caso, use `urlDeArquivo` e deixe o browser buscar sozinho.
 *
 * Fica aqui, e não na tela, pela regra 2 do ESTRUTURA-PASTAS §2: nenhum `fetch`
 * fora de `api/`. E não passa pelo `httpClient` pelo mesmo motivo do
 * `urlDeArquivo` — ele faz `JSON.parse` incondicional e o binário morreria no
 * parse.
 */
export async function baixarArquivo(caminho: string): Promise<Blob> {
  const resposta = await fetch(urlDeArquivo(caminho), { credentials: 'include' });

  if (!resposta.ok) {
    throw new Error(`Não foi possível baixar o arquivo: HTTP ${resposta.status}`);
  }

  return resposta.blob();
}

/**
 * Confere se o arquivo de uma URL assinada ainda existe, ANTES de abri-lo num
 * `<iframe>`. Sem isso, um arquivo que sumiu do Storage aparece para o usuário
 * como o JSON de erro da API dentro do visualizador.
 *
 * Só responde `false` quando o servidor diz com todas as letras que não há
 * arquivo (4xx). Falha de rede ou CORS vira `true`: aí o melhor é deixar o
 * navegador tentar abrir, em vez de esconder um PDF que talvez exista.
 */
export async function arquivoDisponivel(url: string): Promise<boolean> {
  try {
    const resposta = await fetch(url, { method: 'HEAD', credentials: 'same-origin' });
    return !(resposta.status >= 400 && resposta.status < 500);
  } catch {
    return true;
  }
}

/**
 * PDF que vem no CORPO de um POST, não numa URL.
 *
 * ⚠️ Existe porque `urlDeArquivo` acima só serve para GET — não há como um
 * `window.open` mandar POST com corpo JSON —, e o `httpClient` abaixo faz
 * `resposta.text()` + `JSON.parse` incondicionais, que quebrariam no binário.
 *
 * O caso é a prévia de layout: ela renderiza um layout que **ainda não foi
 * salvo**, então não existe id para pôr numa rota GET.
 *
 * Vive aqui, e não na feature, pela Regra 2 do ESTRUTURA-PASTAS §2: nenhum
 * `fetch` fora de `api/`.
 */
export async function pdfPorPost(caminho: string, corpo: unknown): Promise<Blob> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corpo),
  });

  if (!resposta.ok) {
    // O erro continua vindo em JSON mesmo numa rota que produz PDF — é o
    // envelope do filtro de exceção, que roda antes do handler responder.
    const texto = await resposta.text();
    let envelope: CorpoErro['erro'] | undefined;
    try {
      envelope = (JSON.parse(texto) as CorpoErro | undefined)?.erro;
    } catch {
      // Resposta sem envelope (proxy, gateway). O status ainda informa.
    }

    if (resposta.status === 401) notificarPerdaDeSessao();
    throw new ApiError(resposta.status, envelope);
  }

  return resposta.blob();
}

export interface CorpoErro {
  erro: {
    codigo: string;
    mensagem: string;
    detalhes?: Record<string, string[]>;
    requestId: string;
  };
}

/** Erro tipado da API, com o envelope que o backend sempre devolve. */
export class ApiError extends Error {
  readonly status: number;
  readonly codigo: string;
  readonly detalhes?: Record<string, string[]>;
  readonly requestId?: string;

  constructor(status: number, corpo?: Partial<CorpoErro['erro']>) {
    super(corpo?.mensagem ?? 'Erro inesperado ao falar com o servidor.');
    this.name = 'ApiError';
    this.status = status;
    this.codigo = corpo?.codigo ?? `HTTP_${status}`;
    this.detalhes = corpo?.detalhes;
    this.requestId = corpo?.requestId;
  }

  get naoAutenticado(): boolean {
    return this.status === 401;
  }

  get semPermissao(): boolean {
    return this.status === 403;
  }
}

/** Só os códigos que já confirmamos existir nas respostas do better-auth. */
const MENSAGENS_DO_AUTH: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: 'Email ou senha inválidos.',
  // A `LoginPage` intercepta este código e redireciona para a verificação; a
  // mensagem só aparece se o redirect não achar o e-mail (caso raro).
  EMAIL_NOT_VERIFIED: 'Confirme seu e-mail para continuar.',
  // Confirmação de e-mail por código (plugin emailOTP). A `VerificarEmailPage`
  // trata cada um destes de forma específica; isto é o texto de reserva.
  INVALID_OTP: 'Código incorreto. Confira os 6 dígitos e tente de novo.',
  OTP_EXPIRED: 'O código expirou. Peça um novo pelo botão "Reenviar código".',
  TOO_MANY_ATTEMPTS: 'Muitas tentativas seguidas. Peça um código novo e tente de novo.',
};

/**
 * Rotas do better-auth (`/v1/auth/*`, ex.: `sign-in/email`) respondem
 * `{ message, code }` puro — sem o envelope `{ erro: {...} }` que o resto da
 * API sempre devolve. Isso porque essas rotas são middleware cru, fora do
 * filtro de exceção do Nest de propósito (ver `auth.mount.ts` no backend:
 * "ficam fora de tudo — não passam por guard, filtro nem interceptor").
 *
 * Sem isto, `ApiError` nunca achava `corpo.erro` nessas respostas e caía
 * sempre no texto genérico do construtor — mesmo com o servidor tendo dito
 * exatamente o que houve. Era por isso que senha errada no login virava
 * "Erro inesperado ao falar com o servidor." em vez de "Email ou senha
 * inválidos.".
 */
function envelopeDoAuth(corpo: unknown): Partial<CorpoErro['erro']> | undefined {
  const plano = corpo as { message?: string; code?: string } | undefined;
  if (!plano?.message) return undefined;

  return {
    codigo: plano.code ?? 'ERRO_AUTH',
    mensagem: (plano.code && MENSAGENS_DO_AUTH[plano.code]) ?? plano.message,
  };
}

type Ouvinte = () => void;
const ouvintesDe401 = new Set<Ouvinte>();

/**
 * O AuthProvider se registra aqui para reagir ao 401 (limpar cache e mandar
 * para o login). O cliente HTTP não navega sozinho — ele não conhece o router,
 * e acoplá-lo ao router o tornaria inutilizável fora do React.
 */
export function aoPerderSessao(ouvinte: Ouvinte): () => void {
  ouvintesDe401.add(ouvinte);
  return () => {
    ouvintesDe401.delete(ouvinte);
  };
}

let notificando = false;

function notificarPerdaDeSessao(): void {
  // Trava de reentrância: várias requisições em paralelo tomando 401 juntas
  // disparariam N redirects. Um basta.
  if (notificando) return;
  notificando = true;
  for (const ouvinte of ouvintesDe401) ouvinte();
  setTimeout(() => {
    notificando = false;
  }, 1000);
}

/**
 * Mutator do orval. `credentials: 'include'` em TUDO: a sessão web é cookie
 * httpOnly, então não há header `Authorization` para mandar — e sem `include`
 * o cookie simplesmente não viaja.
 */
export async function httpClient<T>(url: string, init: RequestInit = {}): Promise<T> {
  const temCorpo = init.body !== undefined && init.body !== null;
  // FormData precisa que o browser gere o próprio Content-Type (com o
  // boundary do multipart) — forçar application/json aqui faz o body-parser
  // do Nest tentar ler o multipart como JSON e estourar o limite de tamanho.
  const corpoEhFormData = init.body instanceof FormData;

  const metodo = (init.method ?? 'GET').toUpperCase();
  let resposta: Response;
  try {
    resposta = await fetch(`${BASE}${url}`, {
      ...init,
      credentials: 'include',
      headers: {
        ...(temCorpo && !corpoEhFormData ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch (erro) {
    // Nem chegou resposta (rede, servidor fora, CORS). O backend nunca vai
    // saber disto se o navegador não contar.
    //
    // ⚠️ Menos o CANCELAMENTO: o React Query aborta a query de quem sai da
    // tela no meio do carregamento, e isso não é falha nenhuma — reportar
    // encheria os alertas a cada navegação (visto no primeiro teste real).
    const cancelada = init.signal?.aborted === true || (erro instanceof Error && erro.name === 'AbortError');
    if (!cancelada) reportarErro({
      tipo: 'rede',
      mensagem: erro instanceof Error ? erro.message : 'Falha de rede',
      metodo,
      rotaDaApi: url,
    });
    throw erro;
  }

  if (resposta.status === 204) return undefined as T;

  const texto = await resposta.text();
  let corpo: unknown;
  try {
    corpo = texto ? JSON.parse(texto) : undefined;
  } catch (erro) {
    // Sucesso que não é JSON continua sendo erro de verdade. Já o ERRO que não
    // é JSON é o HTML do gateway (502/503/504) — vira o `ApiError` genérico
    // em vez de um SyntaxError que nenhuma tela sabe mostrar.
    if (resposta.ok) throw erro;
    corpo = undefined;
  }

  if (!resposta.ok) {
    const envelope = (corpo as CorpoErro | undefined)?.erro ?? envelopeDoAuth(corpo);
    // Com `requestId`, o erro passou pelo Nest e o backend já o registrou. 5xx
    // SEM ele veio de antes do Nest (gateway, proxy) — só o navegador viu.
    if (resposta.status >= 500 && !envelope?.requestId) {
      reportarErro({
        tipo: 'http',
        mensagem: `HTTP ${resposta.status} sem resposta da API`,
        metodo,
        rotaDaApi: url,
        statusHttp: resposta.status,
        resposta: corpo ?? texto.slice(0, 2_000),
      });
    }
    if (resposta.status === 401) notificarPerdaDeSessao();
    throw new ApiError(resposta.status, envelope);
  }

  return corpo as T;
}

/**
 * Chamadas às rotas cruas do better-auth (`sign-in/email`, `sign-out`, …).
 *
 * Elas NÃO estão no `openapi.json`: são servidas por middleware, não por
 * controller do Nest, então o orval não as gera. Ficam aqui, dentro de `api/`,
 * para a regra de "nenhum fetch fora de api/" continuar valendo.
 */
export function authFetch<T>(caminho: string, corpo?: unknown): Promise<T> {
  return httpClient<T>(`/v1/auth${caminho}`, {
    method: 'POST',
    ...(corpo !== undefined ? { body: JSON.stringify(corpo) } : {}),
  });
}

export default httpClient;
